import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, Row, Col, Card, CardBody, Input, Badge,
    Form, Label, Table
} from "reactstrap";
import {
    FiTrendingUp, FiTrendingDown, FiActivity, 
    FiRefreshCcw, FiArrowRight, FiPieChart, FiDollarSign
} from "react-icons/fi";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Cell, LineChart, Line
} from "recharts";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const ProfitLossReport = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [rawResponse, setRawResponse] = useState(null);

    const [filters, setFilters] = useState({
        filter: { value: "today", label: "Today" },
        from_date: dayjs(),
        to_date: dayjs()
    });

    const filterOptions = [
        { value: "today", label: "Today" },
        { value: "week", label: "Last Week" },
        { value: "month", label: "Last Month" },
        { value: "3months", label: "Last 3 Months" },
        { value: "year", label: "Full Year" },
        { value: "custom", label: "Custom Range" }
    ];

    const loadPNL = async (currentFilters = filters) => {
        dispatch(loadingTrue());
        try {
            const queryParams = new URLSearchParams({
                filter: currentFilters.filter.value,
                from_date: currentFilters.from_date ? currentFilters.from_date.format('YYYY-MM-DD') : "",
                to_date: currentFilters.to_date ? currentFilters.to_date.format('YYYY-MM-DD') : ""
            }).toString();

            const res = await Handler({ method: "GET", url: `/report/profit_loss.php?${queryParams}` });
            if (res?.success) {
                // Machan, api response structure-a 'rawResponse' state-la store panrom
                setRawResponse(res); 
            }
        } catch (err) {
            toast.error("Failed to generate P&L Report");
        } finally {
            dispatch(loadingFalse());
        }
    };

    useEffect(() => {
        if (filters.filter.value !== 'custom') {
            loadPNL();
        }
    }, [filters.filter.value]);

    const handlePeriodChange = (selectedOption) => {
        const newFilters = { ...filters, filter: selectedOption, page: 1 };
        setFilters(newFilters);
        if (selectedOption.value !== 'custom') {
            loadPNL(newFilters);
        }
    };

    // 🔥 Trend Chart Data Transformation
    const trendData = useMemo(() => {
        const labels = rawResponse?.charts?.trend_chart?.labels || [];
        const values = rawResponse?.charts?.trend_chart?.datasets?.[0]?.data || [];
        return labels.map((label, i) => ({ date: label, sales: values[i] }));
    }, [rawResponse]);

    // 🔥 Bar Chart Data Transformation (Breakdown)
    const breakdownData = useMemo(() => {
        const labels = rawResponse?.charts?.bar_chart?.labels || [];
        const values = rawResponse?.charts?.bar_chart?.datasets?.[0]?.data || [];
        const colors = ['#0d6efd', '#fd7e14', '#dc3545', '#0dcaf0', '#6610f2', '#6f42c1', '#e83e8c', '#fd7e14', '#198754'];
        return labels.map((label, i) => ({ name: label, value: values[i], color: colors[i] }));
    }, [rawResponse]);

    const summary = rawResponse?.summary || {};

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            borderRadius: "10px",
            minHeight: "45px",
            border: "1px solid #dee2e6"
        })
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-3 bg-light min-vh-100">
                <div className="d-md-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h3 className="fw-bold m-0 text-dark">Profit & Loss Statement</h3>
                        <p className="text-muted small m-0">Net business realization and operational costs.</p>
                    </div>
                    <div className="d-flex gap-2 mt-3 mt-md-0 align-items-center">
                        <div style={{ width: '180px' }}>
                            <Select
                                options={filterOptions}
                                value={filters.filter}
                                onChange={handlePeriodChange}
                                styles={customSelectStyles}
                            />
                        </div>
                        <Button color="white" className="shadow-sm border rounded-pill px-3 py-2" onClick={() => loadPNL()}>
                            <FiRefreshCcw />
                        </Button>
                    </div>
                </div>

                {filters.filter.value === 'custom' && (
                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <CardBody className="p-4">
                            <Row className="g-3 align-items-end">
                                <Col md={4}>
                                    <Label className="x-small fw-bold">FROM DATE</Label>
                                    <DatePicker format="DD/MM/YYYY" value={filters.from_date} onChange={(val) => setFilters({ ...filters, from_date: val })} slotProps={{ textField: { fullWidth: true, size: 'small' } }} />
                                </Col>
                                <Col md={4}>
                                    <Label className="x-small fw-bold">TO DATE</Label>
                                    <DatePicker format="DD/MM/YYYY" value={filters.to_date} minDate={filters.from_date} onChange={(val) => setFilters({ ...filters, to_date: val })} slotProps={{ textField: { fullWidth: true, size: 'small' } }} />
                                </Col>
                                <Col md={4}>
                                    <Button color="primary" block className="fw-bold rounded-3" style={{ height: '40px', background: '#2e4a3d', border: 'none' }} onClick={() => loadPNL()}>Sync Statement</Button>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                )}

                {/* Summary Row */}
                <Row className="g-3 mb-4">
                    <Col lg={4}>
                        <StatCard title="Gross Sales" value={`₹${summary.gross_sales?.toLocaleString() || 0}`} icon={<FiDollarSign />} color="#0d6efd" bg="#0d6efd15" />
                    </Col>
                    <Col lg={4}>
                        <StatCard title="Operating Cost" value={`₹${summary.total_operating_cost?.toLocaleString() || 0}`} icon={<FiActivity />} color="#6610f2" bg="#6610f215" />
                    </Col>
                    <Col lg={4}>
                        <Card className={`border-0 shadow-sm rounded-4 h-100 ${summary.status === 'profit' ? 'bg-success' : 'bg-danger'} text-white`}>
                            <CardBody className="p-4 d-flex align-items-center justify-content-between">
                                <div>
                                    <small className="opacity-75 text-uppercase fw-bold x-small">Estimated {summary.status?.toUpperCase()}</small>
                                    <h3 className="fw-bold m-0">₹{summary.profit_loss?.toLocaleString() || 0}</h3>
                                </div>
                                {summary.status === 'profit' ? <FiTrendingUp fontSize={35} className="opacity-50" /> : <FiTrendingDown fontSize={35} className="opacity-50" />}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Row className="g-4">
                    <Col lg={7}>
                        <Card className="border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
                            <CardBody className="p-4">
                                <h6 className="fw-bold mb-4 text-muted small text-uppercase ls-1">Sales Realization Trend</h6>
                                <div style={{ width: '100%', height: 320 }}>
                                    <ResponsiveContainer>
                                        <LineChart data={trendData}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                            <XAxis dataKey="date" axisLine={false} tickLine={false} fontSize={10} dy={10} />
                                            <YAxis axisLine={false} tickLine={false} fontSize={10} />
                                            <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                            <Line type="monotone" dataKey="sales" stroke="#0d6efd" strokeWidth={3} dot={{ r: 4, fill: '#0d6efd' }} activeDot={{ r: 6 }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg={5}>
                        <Card className="border-0 shadow-sm rounded-4 mb-4">
                            <CardBody className="p-4">
                                <h6 className="fw-bold mb-4 text-muted small text-uppercase ls-1">Financial Breakdown</h6>
                                <div style={{ width: '100%', height: 320 }}>
                                    <ResponsiveContainer>
                                        <BarChart data={breakdownData} layout="vertical" margin={{ left: 10 }}>
                                            <XAxis type="number" hide />
                                            <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} fontSize={10} width={100} />
                                            <Tooltip cursor={{ fill: 'transparent' }} />
                                            <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={18}>
                                                {breakdownData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Structured Financial Statement */}
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden mt-4">
                    <Table responsive hover className="mb-0 align-middle">
                        <thead className="bg-light">
                            <tr>
                                <th className="ps-4 py-3 text-muted small text-uppercase ls-1">Accounting Heads</th>
                                <th className="text-end pe-4 text-muted small text-uppercase ls-1">Valuation (INR)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="ps-4 font-weight-bold">Total Gross Sales</td>
                                <td className="text-end pe-4 fw-bold">₹{summary.gross_sales?.toLocaleString()}</td>
                            </tr>
                            <tr>
                                <td className="ps-4 text-muted">Less: TDS Deductions</td>
                                <td className="text-end pe-4 text-danger">- ₹{summary.total_tds?.toLocaleString()}</td>
                            </tr>
                            <tr>
                                <td className="ps-4 text-muted">Less: Credit Note Adjustments</td>
                                <td className="text-end pe-4 text-danger">- ₹{summary.total_credit_notes?.toLocaleString()}</td>
                            </tr>
                            <tr className="bg-light">
                                <td className="ps-4 fw-bold text-primary">Net Sales (Realizable)</td>
                                <td className="text-end pe-4 fw-bold text-primary">₹{summary.net_sales?.toLocaleString()}</td>
                            </tr>
                            <tr className="border-top">
                                <td className="ps-4">Operational Purchase Cost</td>
                                <td className="text-end pe-4">- ₹{summary.purchase_cost?.toLocaleString()}</td>
                            </tr>
                            <tr>
                                <td className="ps-4 text-muted">Direct Expenses</td>
                                <td className="text-end pe-4">- ₹{summary.expenses?.toLocaleString()}</td>
                            </tr>
                            <tr>
                                <td className="ps-4 text-muted">Salary Disbursements</td>
                                <td className="text-end pe-4">- ₹{summary.salary_payments?.toLocaleString()}</td>
                            </tr>
                            <tr>
                                <td className="ps-4 text-muted">Agent Commission Payments</td>
                                <td className="text-end pe-4">- ₹{summary.agent_payments?.toLocaleString()}</td>
                            </tr>
                            <tr className="border-top border-dark">
                                <td className="ps-4 fw-bold fs-5 text-uppercase">Net {summary.status}</td>
                                <td className={`text-end pe-4 fw-bold fs-5 ${summary.status === 'profit' ? 'text-success' : 'text-danger'}`}>
                                    ₹{summary.profit_loss?.toLocaleString()}
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </Card>
            </div>
        </LocalizationProvider>
    );
};

const StatCard = ({ title, value, icon, color, bg }) => (
    <Card className="border-0 shadow-sm rounded-4 h-100 bg-white">
        <CardBody className="d-flex align-items-center gap-3 p-4">
            <div style={{
                width: '50px', height: '50px', borderRadius: '15px',
                backgroundColor: bg, color: color,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px'
            }}>
                {icon}
            </div>
            <div>
                <p className="m-0 text-muted x-small fw-bold text-uppercase ls-1">{title}</p>
                <h4 className="m-0 fw-bold" style={{ color: '#2c3e50' }}>{value}</h4>
            </div>
        </CardBody>
    </Card>
);

export default ProfitLossReport;